import { z } from "zod";
import { stamps, IsoDate, IsoDateTime, Money, Uuid } from "./common.js";
import {
  canonicalType,
  priceListSourceType,
  priceListStatus,
  priceSource,
  productDomain,
} from "./enums.js";

// ---------------------------------------------------------------------------
// Price lists (one versioned ingest per upload/paste)
// ---------------------------------------------------------------------------

export const PriceListDto = z.object({
  id: Uuid,
  supplierId: Uuid,
  sourceType: priceListSourceType,
  status: priceListStatus,
  /** Ingest version per supplier — re-upload creates version + 1, history retained. */
  version: z.number().int().min(1),
  effectiveDate: IsoDate.nullable(),
  /** Original file name for uploads; null for pasted text. */
  fileName: z.string().nullable(),
  /** Rate card explicitly assigned at paste time; null when per-sheet/derived. */
  rateCardId: Uuid.nullable(),
  /** Total parsed rows across all sheets. */
  rowCount: z.number().int().nonnegative(),
  ...stamps,
});
export type PriceList = z.infer<typeof PriceListDto>;

/** Per-sheet parse summary shown on the price-list detail screen. */
export const SheetParseSummary = z.object({
  sheetName: z.string(),
  /** Raw data rows encountered on the sheet. */
  rowCount: z.number().int().nonnegative(),
  /** Rows successfully parsed into price rows. */
  parsedCount: z.number().int().nonnegative(),
  /** Rows skipped (headers, blanks, duplicates of an existing card, unparseable). */
  skippedCount: z.number().int().nonnegative(),
  /** Terms code detected on the sheet (incl. unnamed trailing columns), if any. */
  termsCode: z.string().nullable(),
  /** Rate card the sheet was assigned to, if resolved. */
  rateCardId: Uuid.nullable(),
  warnings: z.array(z.string()),
});
export type SheetParseSummary = z.infer<typeof SheetParseSummary>;

/** `GET /api/price-lists/:id` — meta + per-sheet parse summary. */
export const PriceListDetailDto = PriceListDto.extend({
  sheets: z.array(SheetParseSummary),
  /** Populated when status is `failed`. */
  parseError: z.string().nullable(),
});
export type PriceListDetail = z.infer<typeof PriceListDetailDto>;

/** `POST /api/price-lists/paste` body. */
export const PastePriceListInput = z.object({
  supplierId: Uuid,
  text: z.string().min(1),
  effectiveDate: IsoDate.optional(),
  rateCardId: Uuid.optional(),
});
export type PastePriceListInput = z.infer<typeof PastePriceListInput>;

/**
 * Non-file fields of the `POST /api/price-lists/upload` multipart form.
 * `rateCardAssignment` maps sheetName -> rateCardId or termsCode (needed when
 * sheets carry terms codes in unnamed trailing columns or not at all).
 */
export const PriceListUploadMeta = z.object({
  supplierId: Uuid,
  effectiveDate: IsoDate.optional(),
  rateCardAssignment: z.record(z.string(), z.string()).optional(),
});
export type PriceListUploadMeta = z.infer<typeof PriceListUploadMeta>;

/** `POST /api/price-lists/from-key` — file already PUT to storage via presign. */
export const PriceListFromKeyInput = PriceListUploadMeta.extend({
  uploadKey: z.string().min(1),
  filename: z.string().min(1),
});
export type PriceListFromKeyInput = z.infer<typeof PriceListFromKeyInput>;

// ---------------------------------------------------------------------------
// Presigned uploads (browser PUTs the file straight to storage)
// ---------------------------------------------------------------------------

export const PresignRequest = z.object({
  filename: z.string().min(1),
  mime: z.string().nullish(),
  kind: z.enum(["price_list", "buy_list"]),
});
export type PresignRequest = z.infer<typeof PresignRequest>;

export const PresignResponse = z.object({
  uploadKey: z.string(),
  /** Absolute (S3) or same-origin (local dev) URL to PUT the file body to. */
  url: z.string(),
  method: z.literal("PUT"),
});
export type PresignResponse = z.infer<typeof PresignResponse>;

// ---------------------------------------------------------------------------
// Prices (append-only SCD2 rows; current price = validTo === null)
// ---------------------------------------------------------------------------

export const PriceDto = z.object({
  id: Uuid,
  productId: Uuid,
  supplierId: Uuid,
  branchId: Uuid.nullable(),
  rateCardId: Uuid.nullable(),
  /** Ingest that produced this row; null for manual amendments without one. */
  priceListId: Uuid.nullable(),
  listPrice: Money.nullable(),
  discountPct: z.number().nullable(),
  nettPrice: Money,
  uom: z.string().nullable(),
  packQty: z.number().int().nullable(),
  source: priceSource,
  validFrom: IsoDateTime,
  /** Null while the row is current; set when superseded (SCD2 close). */
  validTo: IsoDateTime.nullable(),
  ...stamps,
});
export type Price = z.infer<typeof PriceDto>;

/** `POST /api/prices/:priceId/amend` body — closes the row, inserts a `manual_amend` row. */
export const AmendPriceInput = z.object({
  nettPrice: Money.optional(),
  listPrice: Money.optional(),
  discountPct: z.number().min(0).max(100).optional(),
  reason: z.string().optional(),
});
export type AmendPriceInput = z.infer<typeof AmendPriceInput>;

// ---------------------------------------------------------------------------
// Parsed-rows preview (`GET /api/price-lists/:id/rows`)
// ---------------------------------------------------------------------------

/** A parsed row joined with the product it resolved to and the price row it produced. */
export const PriceRowPreviewDto = z.object({
  id: Uuid,
  priceListId: Uuid,
  /** 1-based row number within the source sheet/text. */
  rowNo: z.number().int().positive(),
  sheetName: z.string().nullable(),
  supplierSku: z.string().nullable(),
  manPartNumber: z.string().nullable(),
  description: z.string(),
  listPrice: Money.nullable(),
  discountPct: z.number().nullable(),
  /** Null on problem rows (e.g. missing/unparseable nett). */
  nettPrice: Money.nullable(),
  uom: z.string().nullable(),
  packQty: z.number().int().nullable(),
  termsCode: z.string().nullable(),
  /** Why this row cannot publish (missing nett price, unknown terms code…). */
  problem: z.string().nullable(),
  // Joined product (null until the row resolves to a canonical product)
  productId: Uuid.nullable(),
  canonicalType: canonicalType.nullable(),
  domain: productDomain.nullable(),
  brandName: z.string().nullable(),
  // Joined price row (null until published)
  priceId: Uuid.nullable(),
  ...stamps,
});
export type PriceRowPreview = z.infer<typeof PriceRowPreviewDto>;
